import { TTicketDetails } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import { ZLotteryDrawEntry } from '@daymade/common-lib/dbJsonModels';

import { oneOfOrUndefined } from '@daymade/common-lib/types';
import { rafflePrizeGroupForEntriesTab } from 'components/account/EntriesTabBar';
import {
  isLotteryGameType,
  raffleSelected,
  tokenTownSelected,
} from 'components/how-it-works/GameSelector';
import NewRaffleTicket from 'components/iceland-raffle/NewRaffleTicket';
import RaffleTicket from 'components/raffle/RaffleTicket';
import ToggleButton from 'components/ToggleButton';
import SZMinilottoTicket from 'components/token-town/SZMinilottoTicketOLD';
import SZRaffleTicket from 'components/token-town/SZRaffleTicketOLD';
import WeeklyDrawLine from 'components/WeeklyDrawLine';
import Link from 'next/link';
import { useState } from 'react';
import css from 'styled-jsx/css';
import { IRaffleLine } from 'types/Draws';
import { colors } from 'utils/constants';
import { PendingEntriesSection } from './PendingEntriesSection';
import { RafflePrizeGroupValues } from './raffle/RafflePrizeGroup';

const styles = css`
  .quantityShowHideButtonContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    color: #444;
    font-weight: 600;
  }

  .quantityContainer {
    display: flex;
    align-items: center;
  }

  .quantityContainer :global(.ticketIcon) {
    margin-left: 4px;
  }

  .linesContainer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    margin-bottom: 15px;
  }

  .linesContainer.stacked {
    margin-bottom: 40px;
  }

  .linesContainer.stacked > *:first-child {
    z-index: 4;
  }

  .linesContainer.stacked > *:nth-child(2) {
    z-index: -1;
    position: absolute;
    top: 10px;
    transform: scale(0.97);
    transition: 0.2s;
  }

  .linesContainer.stacked > *:nth-child(3) {
    z-index: -2;
    position: absolute;
    top: 20px;
    transform: scale(0.93);
    opacity: 0.6;
    transition: 0.2s;
  }

  .linesContainer.stacked > *:nth-child(n + 4) {
    display: none;
  }

  .lineContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .lineContainer:not(:last-of-type) {
    border-bottom: 3px solid #eee;
  }

  :global(.entryList .szMinilottoTicket) {
    width: 44%;
    padding-top: 16%;
  }
`;

const isPendingTicketDifferent = (
  purchasedTicket: Pick<TTicketDetails, 'entries'>,
  pendingTicket: Pick<TTicketDetails, 'entries'> | undefined,
) => {
  if (!pendingTicket) return false;

  // Quick check to see if the lengths are different
  if (purchasedTicket.entries.length !== pendingTicket.entries.length) {
    return true;
  }

  // Otherwise, we have to check if the entries are exactly the same, ignoring any differences in the ordering
  // of the numbers in the entry lines
  const sortEntryLine = (entry: TTicketDetails['entries'][number]) => {
    const numbers =
      entry.type === GameType.LOTTERY
        ? entry.main.sort((a, b) => a - b)
        : [entry.raffle];
    const bonusPart =
      entry.type === GameType.LOTTERY
        ? entry.bonus.sort((a, b) => a - b)
        : [entry.raffle];
    return numbers.join(',') + (bonusPart ? `|${bonusPart}` : '');
  };

  const sortedPurchasedTicketEntries = purchasedTicket.entries
    .map((x) => sortEntryLine(x))
    .sort();

  const sortedPendingTicketEntries = pendingTicket.entries
    .map((x) => sortEntryLine(x))
    .sort();

  // Compare each sorted entry
  for (let i = 0; i < sortedPurchasedTicketEntries.length; i++) {
    if (sortedPurchasedTicketEntries[i] !== sortedPendingTicketEntries[i]) {
      return true;
    }
  }

  return false;
};

interface IEntryList {
  ticket: {
    entries: TTicketDetails['entries'];
    drawType: { code: string; gameType: string };
    drawSchedule?: { prizeStructureId: number };
  };
  pendingTicket?: Pick<TTicketDetails, 'entries'>;
  noGlow?: boolean;
  expandable?: boolean;
  editable?: boolean;
  withPreviousEntriesLink?: boolean;
  onEdit?: () => void;
  numberOfEntriesIsFixed?: boolean;
  noSeperator?: boolean;
  asIcon?: boolean;
  estimatedRenewalDate?: Date;
}

const EntryList = ({
  ticket,
  pendingTicket,
  noGlow = false,
  expandable = false,
  editable = false,
  withPreviousEntriesLink,
  onEdit,
  numberOfEntriesIsFixed,
  noSeperator = undefined,
  asIcon = false,
  estimatedRenewalDate,
}: IEntryList) => {
  const [entriesExpanded, setEntriesExpanded] = useState(false);
  if (!ticket) {
    return null;
  }

  const pendingTicketIfDifferent = isPendingTicketDifferent(
    ticket,
    pendingTicket,
  )
    ? pendingTicket?.entries.every((x) => x.type == GameType.LOTTERY)
      ? (pendingTicket as { entries: Zod.infer<typeof ZLotteryDrawEntry>[] })
      : null
    : null;

  return (
    <div className="entryList fullWidth">
      {expandable && (
        <div className="quantityShowHideButtonContainer">
          <div className="quantityContainer">
            {ticket.entries.length} x ticket
          </div>

          {ticket.entries.length > 1 && (
            <ToggleButton
              chevronReversed={entriesExpanded}
              toggleHandler={() => setEntriesExpanded(!entriesExpanded)}
            >
              {entriesExpanded ? 'Hide Entries' : 'Show All Entries'}
            </ToggleButton>
          )}
        </div>
      )}

      {raffleSelected(ticket.drawType.gameType) && (
        <NewRaffleTicket
          rafflePrizeGroup={ticket.drawSchedule?.prizeStructureId.toString()}
          lines={ticket.entries as IRaffleLine[]}
        />
      )}

      {!raffleSelected(ticket.drawType.gameType) && (
        <div
          className={
            entriesExpanded ||
            (ticket.entries && ticket.entries.length === 1) ||
            !expandable
              ? 'linesContainer'
              : 'linesContainer stacked'
          }
          onClick={() => {
            if (!entriesExpanded && expandable)
              setEntriesExpanded(!entriesExpanded);
          }}
        >
          {!tokenTownSelected(ticket.drawType.code) &&
          pendingTicketIfDifferent ? (
            <PendingEntriesSection
              entries={
                pendingTicketIfDifferent.entries.length > 0
                  ? pendingTicketIfDifferent.entries
                  : null
              }
              asIcon={asIcon}
              estimatedRenewalDate={estimatedRenewalDate}
            />
          ) : null}
          {ticket.entries &&
            ticket.entries.map((entry, idx) => (
              <div key={idx} className="lineContainer">
                {tokenTownSelected(ticket.drawType.gameType) &&
                  (ticket.drawType.code === 'MINILOTTO' ? (
                    <SZMinilottoTicket>{entry}</SZMinilottoTicket>
                  ) : (
                    <SZRaffleTicket>{entry}</SZRaffleTicket>
                  ))}

                {!tokenTownSelected(ticket.drawType.code) &&
                  (isLotteryGameType(ticket.drawType.gameType) &&
                  entry.type === GameType.LOTTERY ? (
                    <WeeklyDrawLine
                      resultOrEntryLine={entry}
                      key={idx}
                      circleNumbers={asIcon ?? false}
                      additionalStylingBonus={
                        asIcon
                          ? `
                      border: 2px solid ${colors.brand.pastel};
                      color: ${colors.brand.pastel};
                      background-color: inherit;
                    `
                          : undefined
                      }
                    />
                  ) : entry.type === GameType.RAFFLE ? (
                    <RaffleTicket
                      key={idx}
                      line={entry.raffle!}
                      style={{ width: '190px' }}
                      rafflePrizeGroup={oneOfOrUndefined(
                        rafflePrizeGroupForEntriesTab(ticket.drawType.code) ||
                          // TODO prizeStructureId needs to be expanded out to its values, so that RaffleTicket uses the correct icon
                          (ticket.drawSchedule?.prizeStructureId as any),
                        ...RafflePrizeGroupValues,
                      )}
                      glowColor={noGlow ? 'NONE' : 'BLUE'}
                    />
                  ) : null)}
              </div>
            ))}
        </div>
      )}

      {ticket.entries &&
        ticket.entries.length > 1 &&
        entriesExpanded &&
        expandable && (
          <ToggleButton
            chevronReversed
            toggleHandler={() => setEntriesExpanded(!entriesExpanded)}
          >
            Hide Entries
          </ToggleButton>
        )}

      {editable && !asIcon && (
        <div className="fullWidth flexRightAligned">
          <a className="underline clickable" onClick={onEdit}>
            {numberOfEntriesIsFixed ? 'Edit' : 'Add or edit'} entries
          </a>
        </div>
      )}

      {withPreviousEntriesLink && (
        <Link href="/account/entries">
          <a
            style={{
              margin: '0 1em 0 0',
              fontWeight: 'bold',
              fontStyle: 'italic',
              float: 'right',
            }}
          >
            View Previous Entries
          </a>
        </Link>
      )}

      <style jsx>{styles}</style>
      <style jsx>{`
        .lineContainer:not(:last-of-type) {
          ${noSeperator
            ? 'border-bottom: none'
            : 'border-bottom: 3px solid #eee;'}
        }
      `}</style>
    </div>
  );
};

export default EntryList;
